/**
 * OCTOPUS — EquipmentMonitorPrint
 *
 * Componente do relatório em PDF. Pode ser usado de DOIS jeitos:
 *
 *  1) Como ROTA standalone (/equipments/:id/monitor/print?period=...&theme=...).
 *     Lê params da URL. Útil para colar a URL diretamente.
 *
 *  2) EMBARCADO dentro de EquipmentMonitorPage como MODAL de pré-visualização:
 *     - Props `embeddedEquipmentId` + `embeddedConfig` substituem a URL.
 *     - `onDone` é chamado quando o usuário fecha o modal (afterprint ou botão
 *       "Fechar"), permitindo desmontar o overlay.
 *
 * Estratégia de impressão (CRÍTICO):
 *   O conteúdo fica VISÍVEL na tela como um modal full-screen com largura fixa
 *   em mm (igual à página A4 paisagem). Isso é essencial porque o Recharts
 *   `<ResponsiveContainer>` usa ResizeObserver para medir o parent — se o
 *   parent estiver invisível/zero, o SVG sai vazio. Com o modal visível e
 *   largura fixa em mm, o Recharts pinta com as dimensões EXATAS da página A4,
 *   e o `@media print` simplesmente esconde o resto do app e imprime o modal.
 *
 *   Auto-dispara window.print() após os dados estabilizarem (~1500ms para o
 *   Recharts terminar de medir e pintar). Usuário sempre pode usar o botão
 *   "Imprimir / Salvar PDF" como fallback.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { MapPin, Gauge, Fuel, Clock, AlertCircle, Download, X, Loader2 } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { useTimezone } from '@/contexts/TimezoneContext';
import TelemetryCharts from '@/components/monitor/TelemetryCharts';
import { KPICard } from '@/components/monitor/KPICard';

const API = process.env.REACT_APP_BACKEND_URL;

// Headers vazios — autenticação herda do axios.defaults setado pelo AuthContext.
const noHeaders = () => ({});

const PERIOD_LABELS = {
  '-1h': 'Última hora',
  '-6h': 'Últimas 6 horas',
  '-24h': 'Últimas 24 horas',
  '-7d': 'Últimos 7 dias',
  '-30d': 'Últimos 30 dias',
  '-90d': 'Últimos 90 dias',
  custom: 'Período customizado',
};

const EquipmentMonitorPrint = ({
  embeddedEquipmentId = null,
  embeddedConfig = null,
  onDone = null,
}) => {
  const isEmbedded = !!(embeddedEquipmentId && embeddedConfig);
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { formatDateTime, timezone } = useTimezone();

  // ---- Resolução de parâmetros (props embedded > query params) -----------
  const equipmentId = embeddedEquipmentId || params.equipmentId;
  const period = embeddedConfig?.period || searchParams.get('period') || '-24h';
  const startParam = embeddedConfig?.start || searchParams.get('start') || period;
  const stopParam = embeddedConfig?.stop || searchParams.get('stop') || 'now()';
  const themeRaw = embeddedConfig?.theme || searchParams.get('theme');
  const theme = themeRaw === 'dark' ? 'dark' : 'light';
  const columnsRaw = embeddedConfig?.columns ?? searchParams.get('columns');
  const columns = Math.max(1, Math.min(2, parseInt(columnsRaw, 10) || 2));
  const chartsParam = embeddedConfig?.charts != null
    ? (Array.isArray(embeddedConfig.charts) ? embeddedConfig.charts.join(',') : String(embeddedConfig.charts))
    : (searchParams.get('charts') || 'speed,rpm,fuel,temperature');
  const selectedCharts = useMemo(() => (
    chartsParam.split(',').map(s => s.trim()).filter(Boolean)
  ), [chartsParam]);

  const timeRange = useMemo(() => ({
    start: startParam,
    stop: stopParam,
    key: `${startParam}|${stopParam}`,
  }), [startParam, stopParam]);

  // ---- State / fetch ------------------------------------------------------
  const [equipment, setEquipment] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loadingEquipment, setLoadingEquipment] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    if (!equipmentId) return undefined;
    let cancelled = false;
    setLoadingEquipment(true);
    axios.get(`${API}/api/equipments/${equipmentId}`, { headers: noHeaders() })
      .then(res => { if (!cancelled) setEquipment(res.data); })
      .catch(err => {
        console.error('Erro equipment:', err);
        if (!cancelled) setFetchError('Não foi possível carregar o equipamento.');
      })
      .finally(() => { if (!cancelled) setLoadingEquipment(false); });
    return () => { cancelled = true; };
  }, [equipmentId]);

  useEffect(() => {
    if (!equipmentId) return undefined;
    let cancelled = false;
    const qs = new URLSearchParams({ start: timeRange.start });
    if (timeRange.stop && timeRange.stop !== 'now()') qs.set('stop', timeRange.stop);
    setLoadingStats(true);
    axios.get(
      `${API}/api/equipments/${equipmentId}/signalk/statistics?${qs.toString()}`,
      { headers: noHeaders(), timeout: 120000 }
    )
      .then(res => { if (!cancelled) setStatistics(res.data); })
      .catch(err => {
        console.error('Erro statistics:', err);
        if (!cancelled) setFetchError('Não foi possível carregar os indicadores.');
      })
      .finally(() => { if (!cancelled) setLoadingStats(false); });
    return () => { cancelled = true; };
  }, [equipmentId, timeRange.start, timeRange.stop]);

  // ---- KPIs (mesma lógica do EquipmentMonitorPage) ------------------------
  const computed = statistics?.computed || {};
  const displayKpis = statistics?.display?.kpis || {};
  const displayValue = (key, fallbackValue = null, fallbackUnit = '') => ({
    value: displayKpis[key]?.value ?? fallbackValue,
    unit: displayKpis[key]?.unit ?? fallbackUnit,
  });
  const totalDistanceDisplay = displayValue('distance', computed.total_distance_km, 'km');
  const avgSpeedDisplay = displayValue('avg_speed', computed.avg_speed_kmh, 'km/h');
  const movingAvgSpeedDisplay = displayValue('moving_avg_speed', computed.moving_avg_speed_kmh, 'km/h');
  const totalFuelDisplay = displayValue('total_fuel', computed.total_fuel_liters, 'L');
  const operatingHoursDisplay = displayValue('operating_hours', computed.operating_hours, 'h');
  const movingTimeDisplay = displayValue('moving_time');

  const fuelTotalsByMotorDisplay = useMemo(() => {
    const displayFuelByMotor = statistics?.display?.fuel_by_motor || {};
    const valueFor = (...paths) => {
      for (const path of paths) {
        const d = displayFuelByMotor[path];
        if (d?.value !== null && d?.value !== undefined) return d.value;
      }
      return null;
    };
    return {
      port: valueFor('propulsion.port.fuel.rate', 'port'),
      starboard: valueFor('propulsion.starboard.fuel.rate', 'starboard'),
      engine1: valueFor('propulsion.engine1.fuel.rate', 'engine1'),
      total: totalFuelDisplay.value,
    };
  }, [statistics?.display?.fuel_by_motor, totalFuelDisplay.value]);

  // ---- Cabeçalho ---------------------------------------------------------
  const periodLabel = useMemo(() => {
    if (period && period !== 'custom') return PERIOD_LABELS[period] || period;
    try {
      const a = formatDateTime(new Date(timeRange.start), { omitSeconds: true });
      const b = formatDateTime(new Date(timeRange.stop), { omitSeconds: true });
      return `${a} → ${b}`;
    } catch (_) {
      return `${timeRange.start} → ${timeRange.stop}`;
    }
  }, [period, timeRange, formatDateTime]);

  const generatedAt = useMemo(() => {
    try { return formatDateTime(new Date(), { omitSeconds: true }); }
    catch (_) { return new Date().toLocaleString('pt-BR'); }
  }, [formatDateTime]);

  const userLabel = user?.full_name || user?.email || 'Usuário';

  // ---- Auto-print + afterprint -------------------------------------------
  const dataReady = !loadingEquipment && !loadingStats;
  const wrapperRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [autoTriggered, setAutoTriggered] = useState(false);

  // Captura o wrapper renderizado via html2canvas e monta um PDF A4 paisagem
  // via jsPDF. Em vez do window.print() (incompatível com Recharts dinâmico),
  // o resultado é um arquivo PDF baixado diretamente — WYSIWYG garantido,
  // texto não-selecionável (vira imagem rasterizada).
  const generatePdf = useCallback(async () => {
    const node = wrapperRef.current;
    if (!node || generating) return;
    setGenerating(true);
    setGenerateError(null);
    try {
      // Resolução: scale=2 dobra a densidade de pixels (qualidade boa para PDF
      // sem inflar muito o arquivo). Background explícito conforme o tema para
      // evitar fundo transparente.
      const bg = theme === 'dark' ? '#0b1220' : '#ffffff';
      const canvas = await html2canvas(node, {
        scale: 2.5,         // mais nitidez nas fontes pequenas (vs 2)
        useCORS: true,
        backgroundColor: bg,
        logging: false,
        letterRendering: true,
        // O wrapper já está com 297mm de largura; html2canvas pega o tamanho
        // real do DOM (windowWidth/Height padrão = viewport, então não força).
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.92);

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();   // 297mm
      const pageHeight = pdf.internal.pageSize.getHeight(); // 210mm
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // O wrapper é fixo em 297mm × 210mm com overflow:hidden, portanto o
      // conteúdo SEMPRE cabe em 1 página. Comparação numérica imgHeight vs
      // pageHeight é inconfiável por arredondamento de pixels no html2canvas
      // (ex.: 210.05mm > 210mm dispara uma 2ª página fantasma com 0.05mm de
      // resto). Forçamos 1 página única, encaixando a imagem na altura exata
      // da página (pequena distorção de fração de mm é imperceptível).
      pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, pageHeight);

      const tagPart = (equipment?.tag || 'equipamento')
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9-]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'equipamento';
      const datePart = new Date()
        .toISOString()
        .slice(0, 16)
        .replace(/[T:]/g, '-');
      pdf.save(`relatorio-${tagPart}-${datePart}.pdf`);
    } catch (err) {
      console.error('Falha ao gerar PDF:', err);
      setGenerateError('Não foi possível gerar o PDF. Tente novamente.');
    } finally {
      setGenerating(false);
    }
  }, [equipment, generating, theme]);

  // Auto-gera o PDF uma vez quando os dados estabilizam (~1500ms para o
  // Recharts terminar de pintar os SVGs antes de capturar).
  useEffect(() => {
    if (!dataReady || autoTriggered) return undefined;
    const t = setTimeout(() => {
      setAutoTriggered(true);
      generatePdf();
    }, 1500);
    return () => clearTimeout(t);
  }, [dataReady, autoTriggered, generatePdf]);

  const handleClose = useCallback(() => {
    if (onDone) onDone();
  }, [onDone]);

  // ---- Calibragem A4 paisagem ---------------------------------------------
  // Página: 297mm × 210mm. Padding interno do wrapper: 8mm de cada lado.
  // Útil: 281mm × 194mm.
  //   Header   ~22mm
  //   KPIs     ~18mm
  //   Footer   ~10mm
  //   Gaps     ~6mm
  //   ----
  //   Fixos:   ~56mm  → sobra ~138mm para gráficos.
  //
  // Com 5 gráficos em 2 colunas → 3 linhas → ~46mm/linha ≈ 174px.
  // Com 5 gráficos em 1 coluna  → 5 linhas → ~27mm/linha ≈ 102px.
  // Damos uma folga para o cabeçalho/legenda dentro do card.
  // Calibrado conservadoramente para o conteúdo total caber em 1 página A4
  // paisagem (210mm úteis): header (~22mm) + KPIs (~16mm) + footer (~10mm) +
  // gaps (~6mm) = 54mm fixos; sobra ~140mm para gráficos. Em 2 cols com 2
  // linhas, ~65mm/linha (card com header + plot + legenda + totalizador no
  // de Combustível). 140px de plot ≈ 37mm → folga suficiente.
  const chartHeight = columns === 1 ? 130 : 140;
  // Largura fixa em px (em vez de "100%"): elimina a dependência do
  // ResizeObserver do Recharts medir o parent dentro do contexto flex+grid
  // do modal — onde a medição vinha falhando e os SVGs saíam vazios.
  // Cálculo: wrapper 297mm (1122px @ 96dpi) − 16mm padding (60px) − gap entre
  // cards. Em 2 cols: ~510px por card. Em 1 col: ~1040px.
  const chartWidth = columns === 1 ? 1040 : 510;
  const themeClass = theme === 'dark' ? 'print-page--dark' : 'print-page--light';
  const rootClass = isEmbedded
    ? `print-overlay ${themeClass}`
    : `print-overlay print-overlay--standalone ${themeClass}`;

  const previewMeta = `Tema: ${theme === 'dark' ? 'escuro' : 'claro'} • Colunas: ${columns} • Gráficos: ${selectedCharts.length}`;

  return (
    <div className={rootClass}>
      <style>{PRINT_CSS}</style>

      {/* Barra de controle (visível só na tela) */}
      <div className="print-toolbar no-print">
        <div className="print-toolbar-info">
          <strong>Pré-visualização do relatório</strong>
          <span className="print-toolbar-meta">{previewMeta}</span>
          <span className="print-toolbar-hint">
            O PDF é gerado automaticamente. Use "Baixar PDF" para regenerar.
          </span>
        </div>
        <div className="print-toolbar-actions">
          {generateError && (
            <span className="print-toolbar-error">{generateError}</span>
          )}
          <button
            type="button"
            className="print-btn print-btn--primary"
            onClick={() => generatePdf()}
            disabled={!dataReady || generating}
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Gerando PDF…</span>
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                <span>Baixar PDF</span>
              </>
            )}
          </button>
          {isEmbedded && (
            <button
              type="button"
              className="print-btn print-btn--ghost"
              onClick={handleClose}
            >
              <X className="h-4 w-4" />
              <span>Fechar</span>
            </button>
          )}
        </div>
      </div>

      {/* Página A4 paisagem com largura fixa em mm — html2canvas captura este nó */}
      <div ref={wrapperRef} className={`print-page-wrapper ${themeClass}`}>
        <div className="print-page">

          {/* Cabeçalho */}
          <header className="print-header">
            <div className="print-header-block">
              <div className="print-brand-row">
                <img src="/assets/octopus-icon.png" alt="Octopus" className="print-logo" />
                <div>
                  <p className="print-brand">Octopus</p>
                  <p className="print-title">Relatório de Monitoramento</p>
                </div>
              </div>
            </div>
            <div className="print-header-block print-header-center">
              <p className="print-equip-name">{equipment?.name || '—'}</p>
              <p className="print-equip-meta">
                <span>{equipment?.tag || '—'}</span>
              </p>
            </div>
            <div className="print-header-block print-header-right">
              <p><span className="print-key">Período:</span> {periodLabel}</p>
              <p><span className="print-key">Fuso:</span> {timezone}</p>
              <p><span className="print-key">Gerado em:</span> {generatedAt}</p>
              <p><span className="print-key">Por:</span> {userLabel}</p>
            </div>
          </header>

          {/* Erro */}
          {fetchError && (
            <div className="print-error">
              <AlertCircle className="h-4 w-4" />
              <span>{fetchError}</span>
            </div>
          )}

          {/* KPIs */}
          <section className="print-kpis">
            <KPICard icon={MapPin} label="Distância" value={totalDistanceDisplay.value}
              unit={totalDistanceDisplay.unit} loading={loadingStats} />
            <KPICard icon={Gauge} label="Vel. Média" value={avgSpeedDisplay.value} unit={avgSpeedDisplay.unit}
              secondaryItems={[{ label: 'Média em movimento', value: movingAvgSpeedDisplay.value, unit: movingAvgSpeedDisplay.unit }]}
              loading={loadingStats} />
            <KPICard icon={Fuel} label="Consumo" value={totalFuelDisplay.value}
              unit={totalFuelDisplay.unit} loading={loadingStats} />
            <KPICard icon={Clock} label="Operação" value={operatingHoursDisplay.value} unit={operatingHoursDisplay.unit}
              secondaryItems={[{ label: 'Tempo em movimento', value: movingTimeDisplay.value, unit: movingTimeDisplay.unit }]}
              loading={loadingStats} />
          </section>

          {/* Gráficos — agora capturados via html2canvas, sem ciclo de print. */}
          <section className="print-charts">
            <TelemetryCharts
              equipmentId={equipmentId}
              period={period}
              timeRange={timeRange}
              fuelTotalsByMotor={fuelTotalsByMotorDisplay}
              totalFuelValue={totalFuelDisplay.value}
              totalFuelUnit={totalFuelDisplay.unit}
              selectedCharts={selectedCharts}
              columns={columns}
              chartHeight={chartHeight}
              chartWidth={chartWidth}
            />
          </section>

          {/* Rodapé */}
          <footer className="print-footer">
            <span>Octopus IoT • octopus.areia.com.br</span>
            <span>Gerado em {generatedAt} por {userLabel}</span>
          </footer>

        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CSS embutido
// ---------------------------------------------------------------------------
//
// Filosofia:
//   - Na TELA: o overlay é um modal full-screen escuro com a página A4 fixa
//     em mm centralizada (preview WYSIWYG). Recharts mede dimensões reais.
//   - No PRINT: @media print esconde tudo exceto o overlay, neutraliza o
//     fundo do modal e empurra a página para os limites da @page.
//
// `print-page-wrapper` tem largura fixa 297mm e padding interno 8mm. Como o
// padding já está aí, `@page` usa margin 0 — assim a coordenação tela/print
// é trivial: o que você vê é o que sai.
//
const PRINT_CSS = `
@page { size: A4 landscape; margin: 0; }

/* ------------------------------------------------------------------ */
/* MODO TELA: overlay full-screen com preview da página A4            */
/* ------------------------------------------------------------------ */
.print-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.85);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: auto;
  padding: 0 0 24px 0;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
}

/* Toolbar fixa no topo do modal */
.print-toolbar {
  position: sticky;
  top: 0;
  z-index: 2;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 20px;
  background: #0f172a;
  color: #e2e8f0;
  border-bottom: 1px solid #1e293b;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  flex-wrap: wrap;
}
.print-toolbar-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.print-toolbar-info strong { font-size: 14px; }
.print-toolbar-meta { font-size: 12px; color: #94a3b8; }
.print-toolbar-hint { font-size: 11px; color: #64748b; }
.print-toolbar-actions { display: flex; gap: 8px; flex-shrink: 0; align-items: center; }
.print-toolbar-error { font-size: 12px; color: #dc2626; margin-right: 8px; }
.print-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid transparent;
  cursor: pointer;
  transition: opacity 0.15s;
}
.print-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.print-btn--primary { background: #2563eb; color: white; }
.print-btn--primary:hover:not(:disabled) { background: #1d4ed8; }
.print-btn--ghost {
  background: transparent;
  color: #e2e8f0;
  border-color: #334155;
}
.print-btn--ghost:hover { background: #1e293b; }

/* Wrapper da página: largura fixa em mm = dimensões reais da A4 paisagem.
   É aqui que o Recharts vai medir; precisa estar visível e com tamanho real. */
.print-page-wrapper {
  width: 297mm;
  max-width: 297mm;
  height: 210mm;        /* altura exata: garante 1 página A4 paisagem */
  overflow: hidden;     /* evita estouro p/ a 2ª página fantasma */
  margin: 20px auto 0 auto;
  padding: 8mm;
  box-sizing: border-box;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  background: #fff;
  color: #0f172a;
}

/* Esconde tooltips do Recharts dentro do modal — eles seguem o mouse e
   apareciam congelados na captura do html2canvas se o cursor estivesse
   sobre algum gráfico no momento da geração. */
.print-overlay .recharts-tooltip-wrapper,
.print-overlay .recharts-tooltip-cursor,
.print-overlay .recharts-active-dot {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
}
/* Bloqueia eventos de mouse nos gráficos dentro do modal, garantindo que
   nenhum hover dispare estados visuais durante a pré-visualização. */
.print-overlay .recharts-wrapper {
  pointer-events: none !important;
}

/* Conteúdo interno da página */
.print-page {
  width: 100%;
  height: 100%;
  font-size: 9pt;
  line-height: 1.3;
  display: flex;
  flex-direction: column;
}

/* ------------------------------------------------------------------ */
/* TEMA CLARO — força as CSS vars do shadcn para light,               */
/* independente do tema global do app                                 */
/* ------------------------------------------------------------------ */
.print-page--light {
  --background: 0 0% 100%;
  --foreground: 222.2 84% 4.9%;
  --card: 0 0% 100%;
  --card-foreground: 222.2 84% 4.9%;
  --popover: 0 0% 100%;
  --popover-foreground: 222.2 84% 4.9%;
  --primary: 222.2 47.4% 11.2%;
  --primary-foreground: 210 40% 98%;
  --secondary: 210 40% 96.1%;
  --secondary-foreground: 222.2 47.4% 11.2%;
  --muted: 210 40% 96.1%;
  --muted-foreground: 215.4 16.3% 46.9%;
  --accent: 210 40% 96.1%;
  --accent-foreground: 222.2 47.4% 11.2%;
  --destructive: 0 84.2% 60.2%;
  --destructive-foreground: 210 40% 98%;
  --border: 214.3 31.8% 91.4%;
  --input: 214.3 31.8% 91.4%;
  --ring: 222.2 84% 4.9%;
  background: #fff;
  color: #0f172a;
}
.print-page--light .print-header { border-bottom: 1px solid #e2e8f0; color: #334155; }
.print-page--light .print-footer { border-top: 1px solid #e2e8f0; color: #64748b; }
.print-page--light .print-key { color: #64748b; }
.print-page--light .print-brand { color: #0f172a; }
.print-page--light .print-title { color: #475569; }
.print-page--light .print-error { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }

/* ------------------------------------------------------------------ */
/* TEMA ESCURO                                                        */
/* ------------------------------------------------------------------ */
.print-page--dark {
  --background: 222.2 84% 4.9%;
  --foreground: 210 40% 98%;
  --card: 222.2 47.4% 11.2%;
  --card-foreground: 210 40% 98%;
  --popover: 222.2 84% 4.9%;
  --popover-foreground: 210 40% 98%;
  --primary: 210 40% 98%;
  --primary-foreground: 222.2 47.4% 11.2%;
  --secondary: 217.2 32.6% 17.5%;
  --secondary-foreground: 210 40% 98%;
  --muted: 217.2 32.6% 17.5%;
  --muted-foreground: 215 20.2% 65.1%;
  --accent: 217.2 32.6% 17.5%;
  --accent-foreground: 210 40% 98%;
  --destructive: 0 62.8% 30.6%;
  --destructive-foreground: 210 40% 98%;
  --border: 217.2 32.6% 17.5%;
  --input: 217.2 32.6% 17.5%;
  --ring: 212.7 26.8% 83.9%;
  background: #0b1220;
  color: #e5e7eb;
}
.print-page--dark .print-header { border-bottom: 1px solid #334155; color: #cbd5e1; }
.print-page--dark .print-footer { border-top: 1px solid #334155; color: #cbd5e1; }
.print-page--dark .print-key { color: #94a3b8; }
.print-page--dark .print-error { background: rgba(127,29,29,.25); color: #fca5a5; border: 1px solid rgba(220,38,38,.4); }

/* ------------------------------------------------------------------ */
/* CONTEÚDO DA PÁGINA                                                 */
/* ------------------------------------------------------------------ */
.print-header {
  display: grid;
  grid-template-columns: 1.1fr 1.4fr 1.1fr;
  gap: 6mm;
  padding-bottom: 2.5mm;
  margin-bottom: 3mm;
  align-items: center;
}
.print-header-block p { margin: 0; line-height: 1.25; }
.print-brand-row { display: flex; align-items: center; gap: 3mm; }
.print-logo { width: 10mm; height: 10mm; object-fit: contain; flex-shrink: 0; }
.print-brand { font-size: 11pt; font-weight: 700; letter-spacing: 0.3px; }
.print-title { font-size: 8pt; }
.print-header-center { text-align: center; }
.print-equip-name { font-size: 12pt; font-weight: 700; }
.print-equip-meta {
  font-size: 8pt;
  display: flex;
  align-items: center;
  gap: 2mm;
  justify-content: center;
  flex-wrap: nowrap;
  margin-top: 1mm;
  line-height: 1;
}
/* Tanto a tag quanto o badge usam EXATAMENTE a mesma técnica de caixa:
   inline-flex com altura fixa e baseline pelo centro. Sem isso, a diferença
   de display (inline-flex vs inline-block) e de padding deslocava o badge. */
.print-equip-meta > span {
  display: inline-flex !important;
  align-items: center;
  line-height: 1;
  height: 5mm;
  box-sizing: border-box;
}
.print-equip-meta > span.print-status {
  padding: 0 1.8mm !important;   /* altura controlada via height: 5mm acima */
}
.print-status {
  font-size: 7pt;
  padding: 0.4mm 1.8mm;
  border-radius: 3px;
  font-weight: 600;
  letter-spacing: 0.2px;
  line-height: 1;
  vertical-align: middle;
  display: inline-block;
}
.print-status--online { background: rgba(34,197,94,.12); color: #16a34a; border: 1px solid rgba(34,197,94,.35); }
.print-status--offline { background: rgba(107,114,128,.12); color: #4b5563; border: 1px solid rgba(107,114,128,.35); }
.print-header-right { text-align: right; font-size: 8pt; }
.print-header-right p { margin: 0.2mm 0; }
.print-key { font-weight: 600; }

.print-error {
  display: flex;
  align-items: center;
  gap: 2mm;
  padding: 1.5mm 2.5mm;
  border-radius: 4px;
  margin-bottom: 2.5mm;
  font-size: 8pt;
}

/* KPIs */
.print-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 2.5mm;
  margin-bottom: 3mm;
}
.print-kpis > * { break-inside: avoid; page-break-inside: avoid; }
.print-kpis [class*="CardContent"],
.print-kpis .p-4 { padding: 3mm 3mm !important; }
.print-kpis .text-lg { font-size: 12pt !important; line-height: 1.15 !important; color: #0f172a !important; font-weight: 700 !important; }
.print-kpis .text-xs { font-size: 8pt !important; line-height: 1.35 !important; color: #475569 !important; }
.print-kpis .text-sm { font-size: 8pt !important; }
.print-kpis .h-5 { width: 4mm !important; height: 4mm !important; }
.print-kpis .p-2 { padding: 1.2mm !important; }
.print-kpis .rounded-lg { border-radius: 4px !important; }
.print-kpis .gap-3 { gap: 2mm !important; }
/* Remove o truncate dos labels — a classe colide com o aliasing do raster em
   fontes pequenas e o final do texto some no PDF. Os labels cabem na largura
   disponível, não precisam de ellipsis. */
.print-kpis .truncate {
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: nowrap !important;
}
/* Garante respiro entre as 3 linhas (label/valor/secundário) do KPICard */
.print-kpis p { margin: 0 0 0.8mm 0 !important; }
.print-kpis p:last-child { margin-bottom: 0 !important; }
.print-kpis .mt-1 { margin-top: 1.2mm !important; }
.print-kpis .space-y-0\\.5 > * + * { margin-top: 0.6mm !important; }
/* Item secundário (linha de baixo): label + valor numa linha. Garantir que
   nenhum trunca. */
.print-kpis .flex.items-baseline { gap: 1.5mm !important; }
.print-kpis .flex.items-baseline span { white-space: nowrap !important; overflow: visible !important; text-overflow: clip !important; }

/* Charts */
.print-charts { width: 100%; flex: 1; }
.print-charts > div { gap: 3mm !important; }
.print-charts > div > * {
  break-inside: avoid;
  page-break-inside: avoid;
}
.print-charts [class*="CardHeader"] {
  padding: 1.5mm 2.5mm 0.5mm 2.5mm !important;
}
.print-charts [class*="CardContent"] {
  padding: 1mm 2mm 1.5mm 2mm !important;
}
.print-charts [class*="CardTitle"] {
  font-size: 9pt !important;
  line-height: 1.2 !important;
}
.print-charts .recharts-wrapper { width: 100% !important; }
.print-charts .recharts-surface { overflow: visible; }
.print-charts .recharts-legend-wrapper {
  font-size: 7pt !important;
  padding-top: 1mm !important;
}
.print-charts .recharts-legend-item { margin-right: 4px !important; }
.print-charts .recharts-legend-item-text {
  font-size: 7pt !important;
  color: #475569 !important;
}
.print-charts .recharts-cartesian-axis-tick text {
  font-size: 7pt !important;
}
.print-charts .mt-4 { margin-top: 2mm !important; }
.print-charts .pt-3 { padding-top: 2mm !important; }
.print-charts .gap-2 { gap: 2mm !important; }
.print-charts .px-3 { padding-left: 2.5mm !important; padding-right: 2.5mm !important; }
.print-charts .py-2 { padding-top: 1mm !important; padding-bottom: 1.6mm !important; }
/* Espaço entre o rótulo (BOMBORDO/BORESTE/...) e o valor dentro de cada item
   do totalizador — sem isso colidem. */
.print-charts .mb-2 { margin-bottom: 1.5mm !important; }
.print-charts .mt-1 { margin-top: 0.6mm !important; }
.print-charts .ml-1 { margin-left: 0.6mm !important; }
/* Itens do totalizador ganham respiro vertical entre rótulo e valor */
.print-charts [class*="bg-muted"] p { margin: 0 !important; line-height: 1.2 !important; }
.print-charts [class*="bg-muted"] p + p { margin-top: 0.4mm !important; }
.print-charts .text-\\[11px\\] { font-size: 7pt !important; }

/* Footer */
.print-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 2mm;
  margin-top: auto;
  font-size: 7.5pt;
}

/* ------------------------------------------------------------------ */
/* MODO PRINT — esconde tudo exceto o overlay, tira o fundo do modal  */
/* ------------------------------------------------------------------ */
@media print {
  /* Esconde TODO o app, menos o overlay */
  body * { visibility: hidden !important; }
  .print-overlay, .print-overlay * { visibility: visible !important; }

  /* Esconde a toolbar */
  .no-print { display: none !important; }

  /* O overlay vira o conteúdo da página (sem fundo escuro de modal) */
  .print-overlay {
    position: absolute !important;
    inset: 0 !important;
    background: white !important;
    padding: 0 !important;
    margin: 0 !important;
    overflow: visible !important;
    display: block !important;
    z-index: 9999 !important;
  }

  /* Wrapper enche a página real e mantém o padding interno como margem */
  .print-page-wrapper {
    width: 100% !important;
    max-width: none !important;
    min-height: auto !important;
    margin: 0 !important;
    padding: 8mm !important;
    box-shadow: none !important;
  }

  html, body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Esconde tooltips do Recharts que ficaram em hover */
  .recharts-tooltip-wrapper,
  .recharts-tooltip-cursor {
    display: none !important;
    visibility: hidden !important;
  }

  /* Force SVGs do Recharts a manter dimensões iguais às do parent durante o
     print. Sem isso, alguns navegadores recalculam o SVG com width/height=0
     ao entrar em @media print, deixando o plot vazio mesmo com o container
     com tamanho correto. */
  .print-page-wrapper .recharts-responsive-container {
    width: 100% !important;
    min-height: 1px !important;
  }
  .print-page-wrapper .recharts-wrapper,
  .print-page-wrapper .recharts-wrapper svg,
  .print-page-wrapper .recharts-surface {
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
  }

  /* Force colors to render (impede "economia de tinta" do Chrome) */
  .print-page-wrapper, .print-page-wrapper * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Reforço de quebra-página dentro de cada card de chart */
  .print-charts > div > * {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
}
`;

export default EquipmentMonitorPrint;
